import { AxiosError, AxiosResponse } from "axios";
import { Button, Container, Form, Stack, Text } from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { OneTimeCode } from "components/OneTimeCode/OneTimeCode";
import { SvgInline } from "components/SvgInline/SvgInline";
import { postHref } from "library/api";
import { useMutation } from "react-query";
import { ActionType } from "./reducer/types";
import { ScreenProps, VerifyOTPResponse } from "./types";

const initialValues = {
  oneTimeCode: "",
};

export type SessionVariables = {
  createSessionUrl: string;
  tokenId: string;
};

export const OtpForm = ({
  state,
  onSuccess,
  dispatch,
  hasContactUsLink,
  onBackClick,
}: Omit<ScreenProps, "errorFooter">) => {
  const verifyMutation = useMutation<
    AxiosResponse<VerifyOTPResponse>,
    AxiosError,
    {
      oneTimeCode: string;
      otpVerifyUrl: string;
    }
  >(({ oneTimeCode, otpVerifyUrl }) =>
    postHref<VerifyOTPResponse>(otpVerifyUrl, {
      otp: oneTimeCode,
    }),
  );

  const onSubmit = async ({ errors, values, setErrors }) => {
    if (Object.keys(errors).length === 0) {
      verifyMutation.mutate(
        { otpVerifyUrl: state.otpVerifyUrl, ...values },
        {
          onSuccess: (response) => {
            switch (response.data.verification_outcome) {
              case "OTP_Mismatch": {
                setErrors({
                  oneTimeCode: "Invalid code",
                });
                break;
              }

              case "OTP_OK": {
                onSuccess();
                break;
              }

              case "OTP_Exceeded_Allowed_Limits": {
                dispatch({ type: ActionType.MAX_TRIES_REACHED_ERROR });
                break;
              }

              default: {
                dispatch({ type: ActionType.ERROR });
              }
            }
          },

          onError() {
            dispatch({ type: ActionType.ERROR });
          },
        },
      );
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      <Stack gap="8">
        <SvgInline name="icon-security-lock" size="92" />
        <Text as="h3" textStyle="heading5" textStyle-sm="heading4">
          Verify your identity
        </Text>
        <OneTimeCode
          otpRecipient={state.maskedMobileNumber as string}
          resendMutationFn={() => postHref(state.otpResendUrl as string)}
          onResendCodeError={() => {
            dispatch({ type: ActionType.ERROR });
          }}
          hasContactUsLink={hasContactUsLink}
        />
        <Container width="100%" width-sm="400">
          {onBackClick ? (
            <FormActions>
              <FormActions.Primary>
                <Button
                  type="submit"
                  width="100%"
                  loading={verifyMutation.isLoading}
                >
                  Next
                </Button>
              </FormActions.Primary>

              <FormActions.Secondary>
                <Button variant="secondary" width="100%" onClick={onBackClick}>
                  Back
                </Button>
              </FormActions.Secondary>
            </FormActions>
          ) : (
            <Button
              type="submit"
              width="160px"
              loading={verifyMutation.isLoading}
            >
              Next
            </Button>
          )}
        </Container>
      </Stack>
    </Form>
  );
};
